import autobind from 'core-decorators/lib/autobind';
import { connect } from 'react-redux';
import { intlShape } from 'react-intl';
import classnames from 'classnames';
import clamp from 'text-overflow-clamp';
import * as dealInfoHelpers from 'shared/utils/deal-info-helpers';
import { infoPositionValues } from 'default-settings';

import { WixConnect } from 'shared/utils/wix-connect';
import {
  isVideosListInfoShownOnHover,
  getVideoInfoPosition,
  isPreviewHover,
  canShowVideoListItemTitle,
  canShowVideoListItemPublisher,
} from 'shared/selectors/app-settings';

import Icon from 'shared/components/icon/icon';
import { SimpleVideoThumbnail } from './simple';

import styles from './thumbnail.styl';

import translations from './translations';


const _propTypes = {
  videoItem: React.PropTypes.object.isRequired,
  isSelected: React.PropTypes.bool,
  nowPlaying: React.PropTypes.bool,
  watched: React.PropTypes.bool,
  width: React.PropTypes.number,
  height: React.PropTypes.number,
  intl: intlShape,
  className: React.PropTypes.string,
  isTitleVisible: React.PropTypes.bool,
  isPublisherVisible: React.PropTypes.bool,
  isPreviewHover: React.PropTypes.bool,
  isDescriptionVisibleOnHoverOnly: React.PropTypes.bool,
  isDescriptionOnVideo: React.PropTypes.bool,
};

const _defaultProps = {
  current: false,
  watched: false,
  width: 160,
  height: 104,
};


@connect(state => ({
  isTitleVisible: canShowVideoListItemTitle(state),
  isPublisherVisible: canShowVideoListItemPublisher(state),
  isPreviewHover: isPreviewHover(state),
  isDescriptionVisibleOnHoverOnly: isVideosListInfoShownOnHover(state),
  isDescriptionOnVideo: getVideoInfoPosition(state) === infoPositionValues.ON_VIDEOS,
}))
@WixConnect(styles)
export default class Thumbnail extends React.Component {

  static propTypes = _propTypes;
  static defaultProps = _defaultProps;

  componentDidMount() {
    this.clamp();
  }

  componentDidUpdate() {
    this.clamp();
  }

  clamp() {
    // description bellow thumbnail with dynamic height is not supported by classic size calculation
    if (this.props.isDescriptionOnVideo) {
      clamp(this.titleNode, { lineClamp: 2 });
    }
  }

  get title() {
    const { videoItem, isTitleVisible } = this.props;
    const { title } = videoItem;
    if (!isTitleVisible) {
      return null;
    }

    return (
      <span ref={node => (this.titleNode = node)}
            styleName="title"
            title={title}>
        {title}
      </span>
    );
  }

  get publisher() {
    const { videoItem, isPublisherVisible } = this.props;
    const { publisher } = videoItem;

    if (!isPublisherVisible) {
      return null;
    }

    return (
      <span styleName="publisher"
            title={publisher}>
        {publisher}
      </span>
    );
  }

  get description() {
    const title = this.title;
    const publisher = this.publisher;
    if (!title && !publisher) {
      return null;
    }

    return (
      <span styleName="description">
        {title}
        {publisher}
      </span>
    );
  }

  get purchaseIcon() {
    const minVideoPrice = dealInfoHelpers.getMinPriceInfo(this.props.videoItem.dealInfo);
    if (!minVideoPrice || this.props.watched) {
      return null;
    }

    return (
      <span styleName="purchase-icon">
        <Icon name="paid" />
      </span>
    );
  }

  get watchedIcon() {
    if (!this.props.watched) {
      return null;
    }

    return (
      <span styleName="watched-icon">
        <Icon name="eye" />
      </span>
    );
  }

  render() {
    const {
      videoItem, isSelected, nowPlaying, width, height, intl, className,
      isDescriptionVisibleOnHoverOnly, isDescriptionOnVideo, isPreviewHover,
    } = this.props;

    const { title, durationStr } = videoItem;
    const { formatMessage } = intl;

    const wrapperStyleNames = classnames('wrapper', {
      selected: isSelected,
      'now-playing': nowPlaying,
      'description-on-video': isDescriptionOnVideo,
      'description-on-hover': isDescriptionVisibleOnHoverOnly,
      'preview-hover': isPreviewHover,
    });

    return (
      <span styleName={wrapperStyleNames}
            style={{ width }}
            className={className}>
        <span styleName="thumbnail"
              title={title}
              data-duration={durationStr}
              data-now-playing={formatMessage(translations.nowPlaying)}>
          <SimpleVideoThumbnail video={videoItem}
                                width={width}
                                height={height} />
          {this.purchaseIcon}
          {this.watchedIcon}
          {isDescriptionOnVideo && this.description}
        </span>
        {!isDescriptionOnVideo && this.description}
      </span>
    );
  }
}

@WixConnect(styles)
export class ClickableThumbnail extends React.Component {

  static propTypes = {
    ..._propTypes,
    videoItem: React.PropTypes.object.isRequired,
    onClick: React.PropTypes.func.isRequired,
    dataHook: React.PropTypes.string,
  };

  static defaultProps = _defaultProps;

  @autobind
  handleClick(event) {
    event.preventDefault();

    const { onClick, videoItem } = this.props;
    onClick(videoItem);
  }

  render() {
    let props = _.pick(this.props, _.keys(_propTypes));
    const { className } = props;
    props = _.omit(props, 'className', 'styles', 'dataHook');

    return (
      <a href=""
         styleName="clickable"
         className={className}
         data-hook={this.props.dataHook}
         onClick={this.handleClick}>
        <Thumbnail {...props} />
      </a>
    );
  }
}



// WEBPACK FOOTER //
// ./src/widget/components/thumbnail/thumbnail.js